import { create } from "zustand";

export type Lang = "ko" | "en" | "ja";
export const LANGS: { code: Lang; label: string }[] = [
  { code: "ko", label: "한국어" },
  { code: "en", label: "English" },
  { code: "ja", label: "日本語" },
];

type Dict = Record<string, Record<Lang, string>>;

const DICT: Dict = {
  "brand.tagline": { ko: "연주 분석 · 코드 악보 · 연습", en: "Analyze · Chords · Practice", ja: "解析 · コード · 練習" },
  "brand.home": { ko: "처음 화면으로", en: "Back to start", ja: "最初の画面へ" },

  // song input
  "input.title": { ko: "곡 입력", en: "Add a song", ja: "曲を追加" },
  "input.drop": { ko: "오디오·영상 파일을 끌어다 놓기", en: "Drag & drop an audio/video file", ja: "音声・動画ファイルをドラッグ＆ドロップ" },
  "input.formats": { ko: "MP3 · WAV · MP4 · MOV · 최대 10분 · 100MB — 클릭해서 선택", en: "MP3 · WAV · MP4 · MOV · up to 10 min · 100MB — click to choose", ja: "MP3 · WAV · MP4 · MOV · 最大10分 · 100MB — クリックで選択" },
  "input.or": { ko: "또는", en: "or", ja: "または" },
  "input.yturl": { ko: "YouTube URL 붙여넣기", en: "Paste a YouTube URL", ja: "YouTube URL を貼り付け" },
  "input.analyze": { ko: "분석", en: "Analyze", ja: "解析" },
  "input.ythint": { ko: "YouTube는 10분 이하 영상만 분석할 수 있어요", en: "YouTube videos must be 10 minutes or shorter", ja: "YouTubeは10分以下の動画のみ解析できます" },
  "input.analyzing": { ko: "분석 중… 키·코드·박자 추출", en: "Analyzing… extracting key, chords, beats", ja: "解析中… キー・コード・拍子を抽出" },
  "input.analyzing_yt": { ko: "분석 중… (YouTube)", en: "Analyzing… (YouTube)", ja: "解析中…（YouTube）" },
  "input.queued": { ko: "분석 대기 중…", en: "Queued…", ja: "解析待ち…" },
  "input.err": { ko: "오류가 발생했어요. 잠시 후 다시 시도해 주세요.", en: "Something went wrong. Please try again in a moment.", ja: "エラーが発生しました。しばらくしてからもう一度お試しください。" },
  "input.toobig": { ko: "파일이 너무 큽니다 (최대 100MB)", en: "File too large (max 100MB)", ja: "ファイルが大きすぎます（最大100MB）" },
  "input.offline": { ko: "오프라인 — 분석은 인터넷 연결이 필요합니다", en: "Offline — analysis needs an internet connection", ja: "オフライン — 解析にはインターネット接続が必要です" },

  // analysis
  "analysis.title": { ko: "분석 결과", en: "Analysis", ja: "解析結果" },
  "analysis.tempo": { ko: "템포", en: "Tempo", ja: "テンポ" },
  "analysis.key": { ko: "키", en: "Key", ja: "キー" },

  // score section
  "score.title": { ko: "코드 악보", en: "Sheet", ja: "譜面" },
  "score.empty": { ko: "파일을 업로드하거나 YouTube 링크를 분석하면 코드 악보가 여기에 표시됩니다.", en: "Upload a file or analyze a YouTube link to see the chord chart here.", ja: "ファイルをアップロードするかYouTubeリンクを解析すると、ここにコード譜が表示されます。" },
  "score.tab": { ko: "타브악보", en: "TAB", ja: "TAB" },
  "score.chord": { ko: "코드", en: "Chords", ja: "コード" },

  // chord grid
  "grid.shapes": { ko: "코드 모양 (예시 운지)", en: "Chord shapes (example fingerings)", ja: "コードの形（運指例）" },
  "grid.empty": { ko: "분석된 코드가 없습니다.", en: "No chords analyzed.", ja: "解析されたコードがありません。" },

  // controls (titles / labels)
  "ctrl.song": { ko: "노래", en: "Song", ja: "曲" },
  "ctrl.score": { ko: "악보", en: "Score", ja: "譜面" },
  "ctrl.play": { ko: "재생 / 일시정지", en: "Play / Pause", ja: "再生 / 一時停止" },
  "ctrl.settings": { ko: "설정", en: "Settings", ja: "設定" },
  "ctrl.edit": { ko: "편집", en: "Edit", ja: "編集" },
  "ctrl.edit_exit": { ko: "편집 종료", en: "Exit edit", ja: "編集を終了" },
  "ctrl.volume": { ko: "볼륨", en: "Volume", ja: "音量" },
  "ctrl.cursorcomp": { ko: "커서 보정", en: "Cursor offset", ja: "カーソル補正" },
  "ctrl.language": { ko: "언어", en: "Language", ja: "言語" },

  // footer
  "footer.privacy": { ko: "개인정보처리방침", en: "Privacy Policy", ja: "プライバシーポリシー" },
  "footer.popular": { ko: "인기 악보", en: "Popular", ja: "人気の楽譜" },
  "footer.about": { ko: "소개", en: "About", ja: "紹介" },
  "footer.contact": { ko: "문의", en: "Contact", ja: "お問い合わせ" },
  "footer.terms": { ko: "이용약관", en: "Terms", ja: "利用規約" },

  // account / saved scores
  "acct.account": { ko: "계정", en: "Account", ja: "アカウント" },
  "acct.logout": { ko: "로그아웃", en: "Log out", ja: "ログアウト" },
  "acct.save": { ko: "현재 악보 저장", en: "Save this sheet", ja: "この譜面を保存" },
  "acct.myscores": { ko: "내 악보", en: "My sheets", ja: "マイ譜面" },
  "acct.login_hint": { ko: "로그인하면 편집한 악보를 저장할 수 있어요", en: "Sign in to save your edited sheets", ja: "ログインすると編集した譜面を保存できます" },
  "save.default": { ko: "{base} 추출 악보", en: "{base} sheet", ja: "{base} コード譜" },
  "save.title_ph": { ko: "악보 이름", en: "Sheet name", ja: "譜面の名前" },
  "save.confirm": { ko: "저장", en: "Save", ja: "保存" },
  "save.cancel": { ko: "취소", en: "Cancel", ja: "キャンセル" },
  "save.ok": { ko: "저장했어요", en: "Saved", ja: "保存しました" },
  "save.fail": { ko: "저장에 실패했어요", en: "Save failed", ja: "保存に失敗しました" },
  "save.nochart": { ko: "저장할 악보가 없어요", en: "Nothing to save", ja: "保存する譜面がありません" },
  "scores.title": { ko: "내 악보", en: "My sheets", ja: "マイ譜面" },
  "scores.empty": { ko: "저장된 악보가 없어요.", en: "No saved sheets yet.", ja: "保存された譜面はまだありません。" },
  "scores.empty_sub": { ko: "YouTube 링크나 오디오를 분석하면\n여기에 코드 악보가 모입니다.", en: "Analyze a YouTube link or audio\nand your chord sheets appear here.", ja: "YouTubeリンクや音声を解析すると\nここにコード譜が集まります。" },
  "scores.limit_h": { ko: "무료 저장 한도에 도달했어요", en: "You've reached the free save limit", ja: "無料保存の上限に達しました" },
  "scores.limit_p": { ko: "Pro로 업그레이드하면 악보를 무제한 저장할 수 있어요.", en: "Upgrade to Pro to save unlimited sheets.", ja: "Proにアップグレードすると無制限に保存できます。" },
  "scores.upgrade": { ko: "업그레이드", en: "Upgrade", ja: "アップグレード" },
  "scores.upgrade_soon": { ko: "결제 기능은 준비 중이에요.", en: "Payments are coming soon.", ja: "決済機能は準備中です。" },
  "scores.load": { ko: "불러오기", en: "Load", ja: "開く" },
  "scores.delete": { ko: "삭제", en: "Delete", ja: "削除" },
  "scores.delete_confirm": { ko: "이 악보를 삭제할까요?", en: "Delete this sheet?", ja: "この譜面を削除しますか？" },
  "scores.close": { ko: "닫기", en: "Close", ja: "閉じる" },
  "scores.count": { ko: "{n} / {max} 저장됨", en: "{n} / {max} saved", ja: "{n} / {max} 保存済み" },
  "scores.untitled": { ko: "제목 없음", en: "Untitled", ja: "無題" },

  // about (home SEO/intro)
  "about.h2": { ko: "Tutti — YouTube·오디오 코드 분석", en: "Tutti — YouTube & audio chord analysis", ja: "Tutti — YouTube・音声コード解析" },
  "about.intro": {
    ko: "Tutti는 YouTube 링크나 오디오·영상 파일을 분석해 곡의 키, 템포(BPM), 박자, 코드 진행을 자동으로 추출하고 기타 코드 악보와 타브(TAB) 악보를 만들어 주는 무료 도구입니다. 분석 결과는 재생 커서와 함께 표시돼 따라 연습하기 좋습니다.",
    en: "Tutti is a free tool that analyzes a YouTube link or an audio/video file to detect the key, tempo (BPM), time signature and chord progression, then builds guitar chord charts and TAB. Results are shown with a playback cursor so you can play along.",
    ja: "Tuttiは、YouTubeリンクや音声・動画ファイルを解析してキー・テンポ(BPM)・拍子・コード進行を自動抽出し、ギターのコード譜とTAB譜を作る無料ツールです。結果は再生カーソルと一緒に表示され、合わせて演奏できます。",
  },
  "about.how_h": { ko: "이렇게 사용하세요", en: "How to use", ja: "使い方" },
  "about.how1": { ko: "오디오·영상 파일을 올리거나 YouTube 링크를 붙여넣고 분석을 누릅니다. (10분 이하)", en: "Upload an audio/video file or paste a YouTube link and hit Analyze. (≤10 min)", ja: "音声・動画ファイルをアップロードするかYouTubeリンクを貼って解析を押します。（10分以下）" },
  "about.how2": { ko: "키·BPM과 함께 코드 악보가 자동으로 그려집니다. 악보/코드 보기를 전환할 수 있어요.", en: "The chord chart is drawn automatically with key & BPM. Switch between chord and TAB views.", ja: "キー・BPMとともにコード譜が自動で描かれます。コード／TAB表示を切り替えられます。" },
  "about.how3": { ko: "재생하면 커서가 현재 위치를 따라가고, 편집 모드에서 음표·주법·코드를 직접 고칠 수 있습니다.", en: "Play and the cursor follows along; in edit mode you can change notes, techniques and chords.", ja: "再生するとカーソルが現在位置を追い、編集モードで音符・奏法・コードを直接編集できます。" },
  "about.feat_h": { ko: "이런 걸 할 수 있어요", en: "What you can do", ja: "できること" },
  "about.feat1": { ko: "코드 악보 만들기 — 마디별 코드 진행을 한눈에", en: "Make chord charts — see the progression by measure", ja: "コード譜を作成 — 小節ごとの進行が一目で" },
  "about.feat2": { ko: "타브(TAB) 악보 — 기타 운지·주법까지 표기", en: "TAB notation — frets and techniques", ja: "TAB譜 — 運指・奏法まで表記" },
  "about.feat3": { ko: "키·BPM·박자 분석 — 곡의 기본 정보를 자동 추출", en: "Key/BPM/meter analysis — automatic song info", ja: "キー・BPM・拍子の解析 — 曲の基本情報を自動抽出" },
  "about.feat4": { ko: "재생 커서 연습 — 노래/악보에 맞춰 따라 연주", en: "Play-along cursor — follow the song or score", ja: "再生カーソル練習 — 曲・譜面に合わせて演奏" },
  "about.faq_h": { ko: "자주 묻는 질문", en: "FAQ", ja: "よくある質問" },
  "about.faq1": { ko: "<b>어떤 곡이든 되나요?</b> 10분 이하의 오디오·영상이면 분석할 수 있습니다. 코드는 오디오 분석 기반 추정이라 곡에 따라 정확도가 다를 수 있어요.", en: "<b>Any song?</b> Any audio/video under 10 minutes. Chords are estimated from audio analysis, so accuracy varies by song.", ja: "<b>どんな曲でも？</b> 10分以下の音声・動画なら解析できます。コードは音声解析による推定のため、曲により精度が異なります。" },
  "about.faq2": { ko: "<b>무료인가요?</b> 네, 별도 가입 없이 무료로 사용할 수 있습니다.", en: "<b>Is it free?</b> Yes, free to use with no sign-up.", ja: "<b>無料ですか？</b> はい、登録不要で無料で使えます。" },
  "about.faq3": { ko: '<b>내 파일은 저장되나요?</b> 업로드한 원본은 분석 직후 삭제되며 저장하지 않습니다. 자세한 내용은 <a href="/privacy">개인정보처리방침</a>을 참고하세요.', en: '<b>Are my files stored?</b> Uploaded originals are deleted right after analysis and never stored. See the <a href="/privacy">Privacy Policy</a>.', ja: '<b>ファイルは保存されますか？</b> アップロードした原本は解析直後に削除され、保存しません。詳しくは<a href="/privacy">プライバシーポリシー</a>をご覧ください。' },

  // editor dock
  "ed.input": { ko: "입력", en: "Input", ja: "入力" },
  "ed.select": { ko: "선택 — 음표를 눌러 선택/편집", en: "Select — tap a note to select/edit", ja: "選択 — 音符をタップして選択/編集" },
  "ed.add": { ko: "추가 — 지판을 눌러 음 추가", en: "Add — tap the fretboard to add notes", ja: "追加 — 指板をタップして音を追加" },
  "ed.range": { ko: "범위 선택 (음을 꾹 눌러도 활성화) — 복사/잘라내기/붙여넣기", en: "Range select (or press & hold a beat) — copy/cut/paste", ja: "範囲選択 (音を長押しでも) — コピー/カット/貼付" },
  "ed.clip": { ko: "복사", en: "Clip", ja: "クリップ" },
  "ed.copy": { ko: "복사", en: "Copy", ja: "コピー" },
  "ed.cut": { ko: "잘라내기", en: "Cut", ja: "カット" },
  "ed.paste": { ko: "붙여넣기 (선택 위치부터 덮어쓰기)", en: "Paste (overwrite from selection)", ja: "貼り付け (選択位置から上書き)" },
  "ed.range_hint": { ko: "드래그해 범위를 선택하세요 · 다른 곳을 탭하면 해제 (편집 잠금)", en: "Drag to select a range · tap elsewhere to clear (editing locked)", ja: "ドラッグで範囲選択 · 別の場所をタップで解除 (編集ロック)" },
  "ed.range_sel": { ko: "{m}마디 선택됨 — 복사/잘라내기 후 위치 이동해 붙여넣기", en: "Measure {m} selected — copy/cut, then move & paste", ja: "{m}小節を選択" },
  "ed.note": { ko: "음표", en: "Note", ja: "音符" },
  "ed.note_len": { ko: "음표 길이", en: "Note length", ja: "音符の長さ" },
  "ed.dot": { ko: "점음표(반박 추가)", en: "Dotted note", ja: "付点音符" },
  "ed.triplet": { ko: "3연음", en: "Triplet", ja: "3連符" },
  "ed.rest": { ko: "쉼표", en: "Rest", ja: "休符" },
  "ed.rest_len": { ko: "쉼표 길이", en: "Rest length", ja: "休符の長さ" },
  "ed.fx": { ko: "주법", en: "Technique", ja: "奏法" },
  "ed.fx_tip": { ko: "주법 (벤드·슬라이드·하모닉스 등)", en: "Techniques (bend, slide, harmonics…)", ja: "奏法（ベンド・スライド・ハーモニクスなど）" },
  "ed.fx_hint": { ko: "선택된 음에 적용 · 같은 주법을 다시 누르면 해제", en: "Applies to the selected note · tap again to remove", ja: "選択中の音に適用 · 同じ奏法を再度押すと解除" },
  "ed.editg": { ko: "편집", en: "Edit", ja: "編集" },
  "ed.delete": { ko: "음 하나 삭제", en: "Delete one note", ja: "音を1つ削除" },
  "ed.adv": { ko: "고급 (코드·박 레벨·리셋)", en: "Advanced (chord, beat level, reset)", ja: "詳細（コード・拍レベル・リセット）" },
  "ed.chord": { ko: "코드", en: "Chord", ja: "コード" },
  "ed.chord_ph": { ko: "예: Am, G/B, Cmaj7", en: "e.g. Am, G/B, Cmaj7", ja: "例: Am, G/B, Cmaj7" },
  "ed.chord_ph_nosel": { ko: "음/박을 먼저 선택하세요", en: "Select a note/beat first", ja: "先に音/拍を選択してください" },
  "ed.chord_apply": { ko: "코드 적용", en: "Apply chord", ja: "コードを適用" },
  "ed.beatlevel": { ko: "박 레벨", en: "Beat level", ja: "拍レベル" },
  "ed.div2": { ko: "박 ÷2 (느린 박)", en: "Beat ÷2 (slower)", ja: "拍 ÷2（遅い拍）" },
  "ed.mul2": { ko: "박 ×2 (빠른 박)", en: "Beat ×2 (faster)", ja: "拍 ×2（速い拍）" },
  "ed.reset": { ko: "리셋", en: "Reset", ja: "リセット" },
  "ed.reset_tip": { ko: "처음 분석 상태로 되돌리기", en: "Reset to the original analysis", ja: "解析直後の状態に戻す" },
  "ed.reset_confirm": { ko: "악보를 처음 분석 상태로 되돌릴까요? 편집한 내용이 모두 사라집니다.", en: "Reset the sheet to the original analysis? All edits will be lost.", ja: "譜面を解析直後の状態に戻しますか？編集内容はすべて失われます。" },
  "ed.done": { ko: "편집 종료", en: "Done", ja: "完了" },
  "ed.warn": { ko: "⚠ {m}번 마디의 박자가 맞지 않아요 (덜 찼거나 넘침)", en: "⚠ Measure {m} has the wrong beat count (under/over filled)", ja: "⚠ {m}小節目の拍が合いません（不足または超過）" },
  "ed.hint_pick": { ko: "악보에서 마디를 눌러 선택하세요 → 지판으로 음을 넣습니다.", en: "Tap a measure in the sheet, then add notes with the fretboard.", ja: "譜面で小節をタップして選択 → 指板で音を入力します。" },
  "ed.hint_add": { ko: "{m}마디 · 추가 모드 — 지판을 누르면 음이 이어 입력됩니다", en: "Measure {m} · Add mode — tap the fretboard to enter notes", ja: "{m}小節 · 追加モード — 指板を押すと音が続けて入力されます" },
  "ed.hint_select": { ko: "{m}마디 · 선택 모드 — 드래그하면 그룹 선택, 음 탭/지판으로 편집", en: "Measure {m} · Select — drag to group-select; tap/fret to edit", ja: "{m}小節 · 選択 — ドラッグで範囲選択、タップ/指板で編集" },
  "ed.sel_rest": { ko: "쉼표", en: "Rest", ja: "休符" },
  "ed.sel_pos": { ko: "{s}번줄 {f}프렛", en: "String {s}, fret {f}", ja: "{s}弦 {f}フレット" },
  "fb.tip": { ko: "{s}번줄 {f}프렛 · {note}", en: "String {s}, fret {f} · {note}", ja: "{s}弦 {f}フレット · {note}" },
  "fb.dead": { ko: "{s}번줄 뮤트(✕)", en: "String {s} mute (✕)", ja: "{s}弦ミュート(✕)" },

  // note/rest duration names (tooltips + inspector)
  "dur.n.1": { ko: "온음표", en: "Whole note", ja: "全音符" }, "dur.n.2": { ko: "2분음표", en: "Half note", ja: "2分音符" },
  "dur.n.4": { ko: "4분음표", en: "Quarter note", ja: "4分音符" }, "dur.n.8": { ko: "8분음표", en: "Eighth note", ja: "8分音符" },
  "dur.n.16": { ko: "16분음표", en: "16th note", ja: "16分音符" }, "dur.n.32": { ko: "32분음표", en: "32nd note", ja: "32分音符" },
  "dur.r.1": { ko: "온쉼표", en: "Whole rest", ja: "全休符" }, "dur.r.2": { ko: "2분쉼표", en: "Half rest", ja: "2分休符" },
  "dur.r.4": { ko: "4분쉼표", en: "Quarter rest", ja: "4分休符" }, "dur.r.8": { ko: "8분쉼표", en: "Eighth rest", ja: "8分休符" },
  "dur.r.16": { ko: "16분쉼표", en: "16th rest", ja: "16分休符" }, "dur.r.32": { ko: "32분쉼표", en: "32nd rest", ja: "32分休符" },

  // technique groups + labels
  "fxgrp.artic": { ko: "이음·꾸밈", en: "Articulation", ja: "アーティキュレーション" },
  "fx.tie": { ko: "이음줄", en: "Tie", ja: "タイ" },
  "fx.grh": { ko: "꾸밈해머", en: "Grace (H)", ja: "装飾(H)" },
  "fx.grsl": { ko: "꾸밈슬라", en: "Grace (sl)", ja: "装飾(sl)" },
  "fxgrp.bend": { ko: "벤드", en: "Bend", ja: "ベンド" },
  "fxgrp.slide": { ko: "슬라이드", en: "Slide", ja: "スライド" },
  "fxgrp.tech": { ko: "주법", en: "Technique", ja: "奏法" },
  "fxgrp.nuance": { ko: "뉘앙스", en: "Nuance", ja: "ニュアンス" },
  "fxgrp.harmonics": { ko: "하모닉스", en: "Harmonics", ja: "ハーモニクス" },
  "fx.b": { ko: "풀벤드", en: "Full bend", ja: "フルベンド" },
  "fx.bh": { ko: "하프벤드", en: "Half bend", ja: "ハーフベンド" },
  "fx.bq": { ko: "쿼터벤드", en: "1/4 bend", ja: "クォーターベンド" },
  "fx.br": { ko: "벤드릴리즈", en: "Bend/release", ja: "ベンド/リリース" },
  "fx.h": { ko: "H/P", en: "H/P", ja: "H/P" },
  "fx.sl": { ko: "슬라", en: "Slide", ja: "スライド" },
  "fx.ss": { ko: "시프트슬라", en: "Shift slide", ja: "シフトスライド" },
  "fx.sib": { ko: "슬인↗", en: "Slide in↗", ja: "スライドイン↗" },
  "fx.sia": { ko: "슬인↘", en: "Slide in↘", ja: "スライドイン↘" },
  "fx.sou": { ko: "슬아웃↗", en: "Slide out↗", ja: "スライドアウト↗" },
  "fx.sod": { ko: "슬아웃↘", en: "Slide out↘", ja: "スライドアウト↘" },
  "fx.v": { ko: "비브", en: "Vibrato", ja: "ビブラート" },
  "fx.vw": { ko: "와이드비브", en: "Wide vib", ja: "ワイドビブ" },
  "fx.tr": { ko: "트릴", en: "Trill", ja: "トリル" },
  "fx.tt": { ko: "태핑", en: "Tapping", ja: "タッピング" },
  "fx.tp": { ko: "트레몰로", en: "Tremolo", ja: "トレモロ" },
  "fx.pm": { ko: "팜뮤트", en: "Palm mute", ja: "パームミュート" },
  "fx.lr": { ko: "레링", en: "Let ring", ja: "レットリング" },
  "fx.st": { ko: "스타카토", en: "Staccato", ja: "スタッカート" },
  "fx.ac": { ko: "악센트", en: "Accent", ja: "アクセント" },
  "fx.x": { ko: "데드음", en: "Dead note", ja: "デッドノート" },
  "fx.nh": { ko: "내추럴HM", en: "Nat. harm", ja: "ナチュラルHM" },
  "fx.ah": { ko: "인공HM", en: "Art. harm", ja: "人工HM" },
  "fx.ph": { ko: "피킹HM", en: "Pinch harm", ja: "ピッキングHM" },
};

function detect(): Lang {
  try {
    const u = new URLSearchParams(location.search).get("lang");
    if (u === "ko" || u === "en" || u === "ja") return u;
    const s = localStorage.getItem("tutti.lang");
    if (s === "ko" || s === "en" || s === "ja") return s;
    const n = (navigator.language || "").slice(0, 2);
    return n === "ko" ? "ko" : n === "ja" ? "ja" : "en";
  } catch {
    return "ko";
  }
}

interface LangState { lang: Lang; setLang: (l: Lang) => void }
export const useLang = create<LangState>((set) => ({
  lang: detect(),
  setLang: (lang) => {
    try { localStorage.setItem("tutti.lang", lang); } catch { /* ignore */ }
    try { document.documentElement.lang = lang; } catch { /* ignore */ }
    set({ lang });
  },
}));

// set <html lang> on first load
try { document.documentElement.lang = useLang.getState().lang; } catch { /* ignore */ }

// hook: returns a translator that re-renders when the language changes.
// supports {name} interpolation: t("ed.warn", { m: "1, 2" })
export function useT() {
  const lang = useLang((s) => s.lang);
  return (key: string, params?: Record<string, string | number>) => {
    let s = DICT[key]?.[lang] ?? DICT[key]?.ko ?? key;
    if (params) for (const k in params) s = s.split(`{${k}}`).join(String(params[k]));
    return s;
  };
}
